<template>
  <div class="ex-checkpoint-header-wrapper">
    <!-- v2 redesign layout — single header bar, no criteria section -->
    <div v-if="v2" class="ex-checkpoint-header--v2">
      <div class="ex-checkpoint-v2-bar">
        <Breadcrumbs
          :breadcrumbsList="props.breadcrumbs"
          @onClickBreadcrumb="handleBreadcrumbClick"
        />
        <div class="ex-checkpoint-v2-right">
          <PeopleAvatarsButton
            v-if="assignees.length"
            :avatars="assignees"
            :maxVisible="5"
            size="medium"
            :isHoverEnable="false"
            :isShowTooltip="true"
            :allowDrillDown="false"
          />
          <div ref="levelPickerTrigger" class="ex-level-pill">
            <button
              type="button"
              class="level-pill-trigger"
              :class="[levelToneClass, { open: isLevelPickerOpen, disabled: !levelEditable }]"
              :aria-expanded="isLevelPickerOpen"
              :aria-disabled="!levelEditable"
              :disabled="!levelEditable"
              aria-haspopup="listbox"
              @click.stop="toggleLevelPicker"
            >
              <span class="level-pill-label">{{ selectedLevel && selectedLevel.name }}</span>
              <svg
                v-if="levelEditable"
                class="level-pill-chevron"
                :class="{ 'is-open': isLevelPickerOpen }"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <ul
              v-if="isLevelPickerOpen"
              class="level-pill-dropdown"
              role="listbox"
              aria-label="Checkpoint level"
            >
              <li
                v-for="lvl in levelItems"
                :key="`v2-level-${lvl.id}`"
                role="option"
                tabindex="0"
                class="level-pill-item"
                :class="[levelTone(lvl), { 'is-selected': isLevelSelected(lvl) }]"
                :aria-selected="isLevelSelected(lvl)"
                @click.stop="handleLevelItemActivate(lvl)"
                @keydown.enter.prevent="handleLevelItemActivate(lvl)"
                @keydown.space.prevent="handleLevelItemActivate(lvl)"
              >
                <span>{{ lvl.name }}</span>
                <svg
                  v-if="isLevelSelected(lvl)"
                  class="level-pill-check"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.3333 4L6 11.3333L2.66667 8"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </li>
            </ul>
          </div>
          <div class="ex-checkpoint-v2-actions" role="toolbar" aria-label="Checkpoint actions">
            <span class="ex-checkpoint-v2-status" :class="statusBadgeClass">{{
              statusBadgeLabel
            }}</span>
            <button
              type="button"
              class="ex-checkpoint-v2-actions__chatter"
              aria-label="Open chatter"
              @click="onChatterClick"
            >
              <ChatterIcon />
            </button>
            <CheckpointDropDownMenu
              :key="`checkpoint-menu-${checkpointData && checkpointData.id ? checkpointData.id : status}`"
              :status="menuStatus"
              :menu-open="false"
              :horizontal="true"
              :activate-disabled="activateMenuDisabled"
              :locked="operationsMenuLocked"
              @action="handleCheckpointMenuAction($event)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Legacy layout (default) -->
    <div v-else class="ex-checkpoint-header">
      <div class="ex-checkpoint-crumb">
        <Breadcrumbs
          :breadcrumbsList="props.breadcrumbs"
          @onClickBreadcrumb="handleBreadcrumbClick"
        />
        <div class="ex-right-side-checkpoint">
          <div
            class="hide-left-border center-vertical ex-chatter-type-icon"
            @click="onChatterClick"
          >
            <v-img class="chatter-icon" :src="require('@components/assets/ChatterIcon.svg')" />
          </div>
          <Button
            v-if="checkpointState === 'Editable'"
            size="small"
            :outline="'outline-hover'"
            label="Edit"
            class="ex-edit-checkpoint"
            @onClick="onEditClick"
          />
        </div>
      </div>
      <CheckpointLevelBand
        v-if="checkpointState !== 'Edit'"
        class="ex-level-band"
        :level="readiness_level.name"
      />
      <div v-if="checkpointState === 'Edit'" class="ex-checkpoint-picker">
        <CheckpointLevelPicker
          label="Apprentice Level"
          :levels="levelItems"
          :value="selectedLevel"
          @item-selected="resetLevel"
          :class="[
            'add-checkpoint-level-picker',
            { 'add-checkpoint-level-picker-dirty': isLevelDirty },
          ]"
        />
        <div class="ex-right-buttons">
          <Button
            size="small"
            :outline="'outline-hover'"
            label="Discard"
            class="ex-discard-checkpoint"
            @onClick="onDiscardClick"
          />
          <Button
            size="small"
            :fullwidth="isSaveDisabled ? 'disable' : 'hover'"
            :label="'Save'"
            class="ex-save-checkpoint"
            @onClick="onSaveClick"
            :loading="isSaving ? 'left' : null"
          />
        </div>
      </div>
      <div v-if="checkpointState === 'Edit' && isStorybook" class="ex-checkpoint-criteria">
        <CriteriaInput
          class="check-criteria-input mb-10"
          :criteria="criteriaInput"
          :criteriaPrefix="criteriaPrefix"
          :dropdownItems="dropdownItems"
          :isInputDirty="isInputDirty"
          :isPrefixDirty="isPrefixDirty"
          @update:criteria="criteriaInput = $event"
          @update:criteriaPrefix="criteriaPrefix = $event"
        />
      </div>
      <div
        v-if="checkpointState === 'Edit' && !isStorybook"
        class="ex-checkpoint-criteria checkpoint"
      >
        <div class="">
          <ExTextInput
            label="Criteria*"
            placeholder="Add criteria"
            :value="criteriaInput"
            required
            dropdownSide="left"
            :dropdownItems="dropdownItems"
            :class="[
              'add-criteria',
              { 'input-dirty': isInputDirty, 'prefix-dirty': isPrefixDirty },
            ]"
            :showCustomIcon="true"
            @dropdown-input="handlePrefixChange"
            :selectedItem="criteriaPrefix"
            @input="handleCriteriaInput"
            :editModal="true"
          ></ExTextInput>
        </div>
      </div>
      <div v-if="checkpointState === 'Read Only'" class="ex-checkpoint-header-criteria">
        {{ criteriaPrefix }} {{ criteria }}
      </div>
      <div v-if="checkpointState === 'Editable'" class="ex-checkpoint-header-criteria-editable">
        <span class="ex-checkpoint-header-criteria-editable-label">Inspection Criteria</span>
        <span class="ex-checkpoint-header-criteria-editable-criteria">
          <span class="prefix">{{ criteriaPrefix }}</span> {{ criteria }}</span
        >
      </div>
      <!-- <div v-if="checkpointState === 'Editable'" class="ex-checkpoint-header-instructions">
        {{ instructions }}
      </div> -->
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, watch, onUnmounted } from "vue";
import Breadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import CheckpointLevelBand from "@components/CheckpointLevelBand/CheckpointLevelBand.vue";
import Button from "@components/buttons/Button.vue";
import CheckpointLevelPicker from "@components/CheckpointLevelPicker/CheckpointLevelPicker.vue";
import CriteriaInput from "@components/CriteriaInput/CriteriaInput.vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import CheckpointDropDownMenu from "@components/CheckpointDropDownMenu/CheckpointDropDownMenu.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import { changeCheckpointV2 } from "@/api";
import { isCheckpointActivateDisabled } from "@/utils/draftCheckpointValidity";
import { isCheckpointEditLockHeld } from "@/utils/checkpointEditLock";
import { formatCriteriaPrefix } from "@components/utils/formatCriteriaPrefix";
import store from "@/store";

// Loading state for save button
const isSaving = ref(false);

const props = defineProps({
  breadcrumbs: {
    type: Array,
    default: () => [
      {
        text: "Checklist",
        href: "",
      },
      {
        text: "Checkpoint",
        href: "",
        disabled: true,
      },
    ],
  },
  state: {
    type: String,
    validator: (value) => ["Read Only", "Editable", "Edit"].includes(value),
    default: "Read Only",
  },
  readiness_level: {
    type: Object,
    default: () => ({
      id: 1,
      name: "Apprentice",
      value: "apprentice",
    }),
  },
  criteria: {
    type: String,
    default: "",
  },
  instructions: {
    type: String,
    default: "",
  },
  checkpointPrefix: {
    type: String,
    default: "Can",
  },
  isStorybook: {
    type: Boolean,
    default: false,
  },
  // Props needed for API call
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointLevel: {
    type: [Number, String],
    default: 1,
  },
  checkpointStatus: {
    type: String,
    default: "active",
  },
  checkpointInstructions: {
    type: String,
    default: "",
  },
  // v2 redesign
  v2: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    validator: (value) => ["draft", "active", "retired", "archived"].includes(value),
    default: "draft",
  },
  assignees: {
    type: Array,
    default: () => [],
  },
  checkpointData: {
    type: Object,
    default: null,
  },
  /** Feature flag idea-MDP-452: draft activation validation on the status menu. */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
  /** When false, v2 level pill is display-only (viewer / not locked by self). */
  levelEditable: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits([
  "input",
  "reset-level",
  "onSave",
  "update:state",
  "breadcrumb-click",
  "update:criteriaInput",
  "update:criteriaPrefix",
  "update:level",
  "checkpoint-updated",
  "open-chatter",
  "open-actions",
]);

const criteriaInput = ref(props.criteria);
const criteriaPrefix = ref(props.checkpointPrefix || "Can");
const dropdownItems = ref(["Can", "Does", "Demonstrated", "Proved"]);
const levelItems = ref([
  { id: 1, name: "Apprentice", value: "apprentice" },
  { id: 2, name: "Professional", value: "professional" },
  { id: 3, name: "Coach", value: "coach" },
  { id: 4, name: "Master", value: "master" },
]);
const checkpointState = ref(props.state);

// Store initial values to track "dirty" state
const initialCriteria = ref(props.criteria);
const initialCriteriaPrefix = ref(props.checkpointPrefix || "Can");
const initialLevel = ref(props.readiness_level);
const selectedLevel = ref(props.readiness_level);

watch(
  () => props.state,
  (newState) => {
    emit("update:state", newState);
    checkpointState.value = newState;
    // Capture initial values from PROPS when entering Edit mode
    if (newState === "Edit") {
      initialCriteria.value = props.criteria;
      initialCriteriaPrefix.value = props.checkpointPrefix || "Can";
      initialLevel.value = props.readiness_level;
      // Sync local refs with props to ensure clean state
      criteriaInput.value = props.criteria;
      criteriaPrefix.value = props.checkpointPrefix || "Can";
      selectedLevel.value = props.readiness_level;
    }
  }
);

watch(
  () => props.checkpointPrefix,
  (newPrefix) => {
    if (newPrefix) {
      criteriaPrefix.value = newPrefix;
      // Don't update initial value during Edit mode - we want to track changes!
    }
  }
);

watch(
  () => props.criteria,
  (newCriteria) => {
    criteriaInput.value = newCriteria;
    // Don't update initial value during Edit mode - we want to track changes!
  }
);

watch(
  () => props.readiness_level,
  (nextLevel) => {
    if (!nextLevel) return;
    selectedLevel.value = nextLevel;
    if (checkpointState.value !== "Edit") {
      initialLevel.value = nextLevel;
    }
  },
  { deep: true }
);

const resetLevel = (newLevel) => {
  selectedLevel.value = newLevel;
};

// 🔹 v2 header level pill (custom — replaces CheckpointLevelPicker in the v2 bar)
const isLevelPickerOpen = ref(false);
const levelPickerTrigger = ref(null);

function toggleLevelPicker() {
  if (!props.levelEditable) return;
  if (!isLevelPickerOpen.value) {
    isLevelPickerOpen.value = true;
    document.addEventListener("click", closeLevelPickerOutside, { once: true });
  } else {
    isLevelPickerOpen.value = false;
  }
}

function closeLevelPickerOutside(e) {
  if (!levelPickerTrigger.value || !levelPickerTrigger.value.contains(e.target)) {
    isLevelPickerOpen.value = false;
  }
}

function isLevelSelected(item) {
  return selectedLevel.value && String(selectedLevel.value.id) === String(item.id);
}

function selectLevelV2(item) {
  isLevelPickerOpen.value = false;
  resetLevel(item);
  if (item?.name) {
    emit("update:level", item.name);
  }
}

function handleLevelItemActivate(item) {
  if (!isLevelSelected(item)) {
    selectLevelV2(item);
  }
}

function levelTone(item) {
  const key = ((item && item.name) || "").toLowerCase();
  if (key.includes("apprentice")) return "tone-apprentice";
  if (key.includes("professional")) return "tone-professional";
  if (key.includes("coach")) return "tone-coach";
  if (key.includes("master")) return "tone-master";
  return "tone-neutral";
}

const levelToneClass = computed(() =>
  selectedLevel.value ? levelTone(selectedLevel.value) : "tone-apprentice"
);

onUnmounted(() => {
  document.removeEventListener("click", closeLevelPickerOutside);
});

// 🔹 Computed property to check if input is dirty (for highlighting)
const isInputDirty = computed(() => {
  const criteriaIsDirty = criteriaInput.value !== initialCriteria.value;
  return criteriaIsDirty;
});

// 🔹 Computed property to check if prefix is dirty (for prefix highlighting)
const isPrefixDirty = computed(() => {
  const isDirty = criteriaPrefix.value !== initialCriteriaPrefix.value;
  return isDirty;
});

// 🔹 Computed property to check if level is dirty
const isLevelDirty = computed(() => {
  return selectedLevel.value?.id !== initialLevel.value?.id;
});

// 🔹 Computed property to disable Save button based on dirty state
const isSaveDisabled = computed(() => {
  const criteriaIsEmpty = !criteriaInput.value || criteriaInput.value.trim() === "";
  const prefixIsDirty = criteriaPrefix.value !== initialCriteriaPrefix.value;
  const criteriaIsDirty = criteriaInput.value !== initialCriteria.value;
  const levelIsDirty = isLevelDirty.value;

  // Disable if criteria is empty
  if (criteriaIsEmpty) {
    return true;
  }

  // Disable if nothing has changed (not dirty)
  if (!prefixIsDirty && !criteriaIsDirty && !levelIsDirty) {
    return true;
  }

  // Enable if something changed and criteria is not empty
  return false;
});

// 🔹 Handle criteria input changes
const handleCriteriaInput = (value) => {
  criteriaInput.value = value;
  emit("update:criteriaInput", value);
};

// 🔹 Handle prefix changes
const handlePrefixChange = (value) => {
  criteriaPrefix.value = value;
  emit("update:criteriaPrefix", value);
};

// 🔹 Switch to Edit mode
const onEditClick = () => {
  emit("update:state", "Edit");
  checkpointState.value = "Edit";
  // Store initial values from PROPS (source of truth) when entering Edit mode
  initialCriteria.value = props.criteria;
  initialCriteriaPrefix.value = props.checkpointPrefix || "Can";
  initialLevel.value = props.readiness_level;
  // Sync local refs with props to ensure clean state
  criteriaInput.value = props.criteria;
  criteriaPrefix.value = props.checkpointPrefix || "Can";
  selectedLevel.value = props.readiness_level;
};

// 🔹 Discard edits → revert all changes and go back to Editable
const onDiscardClick = () => {
  // Revert criteria, prefix, and level to their initial values
  criteriaInput.value = initialCriteria.value;
  criteriaPrefix.value = initialCriteriaPrefix.value;
  selectedLevel.value = initialLevel.value;

  // Emit updates to notify parent of reverted values
  emit("update:criteriaInput", initialCriteria.value);
  emit("update:criteriaPrefix", initialCriteriaPrefix.value);

  // Change state back to Editable
  emit("update:state", "Editable");
  checkpointState.value = "Editable";
};

// 🔹 Save → call API with loader, emit data and go back to Editable
const onSaveClick = async () => {
  // If checkpoint IDs are available, call the API
  if (props.checkpointId && props.responsibilityId) {
    isSaving.value = true;

    try {
      // Safely parse accountId from localStorage
      let accountId = null;
      try {
        const storedAccountId = localStorage.getItem("currentAccountID");
        if (storedAccountId) {
          accountId = JSON.parse(storedAccountId);
        }
      } catch (parseError) {
        console.error("Error parsing currentAccountID from localStorage:", parseError);
      }

      if (accountId) {
        const payload = {
          criteria: criteriaInput.value,
          prefix: formatCriteriaPrefix(criteriaPrefix.value),
          instructions: props.checkpointInstructions || "<p></p>",
          proficiency_level: selectedLevel.value?.id || props.checkpointLevel,
          status: props.checkpointStatus,
          account: accountId,
          responsibility: props.responsibilityId,
        };

        const response = await changeCheckpointV2(
          accountId,
          props.responsibilityId,
          props.checkpointId,
          payload
        );

        // Emit event with API response data to update parent (emit even if data is empty for consistent state)
        emit("checkpoint-updated", response?.data || null);
      }
    } catch (error) {
      console.error("Error updating checkpoint:", error);
    } finally {
      isSaving.value = false;
    }
  }

  // Emit the original onSave event for backwards compatibility
  emit("onSave", {
    criteria: criteriaInput.value,
    prefix: criteriaPrefix.value,
    level: selectedLevel.value,
  });
  emit("update:state", "Editable");
  checkpointState.value = "Editable";
};

// 🔹 Handle breadcrumb click
const handleBreadcrumbClick = (breadcrumb) => {
  emit("breadcrumb-click", breadcrumb);
};

// 🔹 Handle chatter icon click
const onChatterClick = () => {
  const data = props.checkpointData || {};
  emit("open-chatter", {
    id: props.checkpointId ?? data.id,
    level: props.readiness_level?.name || data.level || "Apprentice",
    criteria: props.criteria || data.criteria,
    prefix: props.checkpointPrefix || data.prefix,
    instructions: props.checkpointInstructions || data.instructions,
  });
};

// 🔹 v2 header actions — status/menu (previously CheckpointCardActions)
const menuData = computed(() => {
  if (props.checkpointData) return props.checkpointData;
  return { status: props.status };
});

const activateMenuDisabled = computed(() => {
  const flagOn = props.ideaMDP452 === true || Boolean(store.getters["featureFlags/ideaMDP452"]);
  if (!flagOn) {
    return false;
  }
  return isCheckpointActivateDisabled(menuData.value);
});

function capitalizeStatus(status, data) {
  if (
    data &&
    (data.archived === true || (status && status.toString().toLowerCase() === "archived"))
  ) {
    return "Archived";
  }
  if (!status) return "Draft";
  const normalized = status.toString();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

const menuStatus = computed(() => capitalizeStatus(menuData.value?.status, menuData.value));

/** Rule 5: replace header ⋮ with lock while any Access holds the edit lock. */
const operationsMenuLocked = computed(() => isCheckpointEditLockHeld(menuData.value));

// 🔹 v2 status pill — matches Figma spec exactly (Draft/Active/Retired/Archived)
const statusBadgeLabel = computed(() => {
  const normalized = (menuStatus.value || "Draft").toLowerCase();
  if (normalized === "archived") return "Archived";
  if (normalized === "active") return "Active";
  if (normalized === "retired") return "Retired";
  return "Draft";
});

const statusBadgeClass = computed(() => {
  const normalized = (menuStatus.value || "Draft").toLowerCase();
  return `ex-checkpoint-v2-status--${normalized === "archived" || normalized === "active" || normalized === "retired" ? normalized : "draft"}`;
});

function handleCheckpointMenuAction(item) {
  if (item?.disabled || (item?.value === "activate" && activateMenuDisabled.value)) {
    return;
  }
  emit("open-actions", {
    item,
    data: menuData.value,
    showDialog: true,
  });
}
</script>
<style scoped>
.checkpoint ::v-deep .v-text-field {
  padding-top: 0px !important;
}

.checkpoint
  ::v-deep
  .level-picker.v-text-field
  .v-input__control
  .v-input__slot
  .v-input__append-inner,
.checkpoint
  ::v-deep
  .level-picker.v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  margin-top: 4px;
}
::v-deep .add-criteria .left-can-prepand {
  min-width: 65px;
  top: -10px;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
::v-deep .add-criteria .left-demonstrated-prepand {
  min-width: 132px;
  top: -10px;
}
::v-deep .add-criteria .left-proved-prepand {
  min-width: 80px;
  top: -10px;
}
::v-deep
  .add-criteria.ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -60px !important;
}
::v-deep
  .add-criteria.ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -10px !important;
}

/* Purple/lavender background when only criteria is dirty (text input area only, not prefix dropdown) */
/* For "Can" prefix (default ~65px + 6px gap) */
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 71px,
    #d9d6fe 71px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Demonstrated" prefix (~132px + 6px gap) */
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 143px,
    #d9d6fe 143px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Proved" prefix (~80px + 6px gap) */
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 91px,
    #d9d6fe 91px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Purple/lavender background when only prefix is dirty (only prefix dropdown, not text area) */
/* For "Can" prefix */
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 79px,
    white 65px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-select__selections {
  background: transparent !important;
}

/* For "Demonstrated" prefix */
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 147px,
    white 137px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-select__selections {
  background: transparent !important;
}

/* For "Proved" prefix */
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 95px,
    white 85px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .code-select-wrapper {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  .v-input__slot {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  fieldset {
  background: transparent !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-select__selections {
  background: transparent !important;
}

/* Purple/lavender background when BOTH prefix AND criteria are dirty (entire input) */
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background-color: #d9d6fe !important;
  z-index: 0 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty.input-dirty
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .code-select-wrapper {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control fieldset {
  background: transparent !important;
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important;
}

::v-deep .add-criteria.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important;
}

::v-deep
  .add-criteria.prefix-dirty
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background-color: transparent !important;
}
::v-deep .add-checkpoint-level-picker-dirty .picker-trigger {
  border-radius: 8px !important;
  border: 4px solid #7f56d9 !important;
}
::v-deep .add-checkpoint-level-picker-dirty .picker-trigger.open {
  box-shadow: 0 0 0 3px #e9d7ff;
  border: 1px solid #d6bbfb !important;
}

/* ── v2 redesign ─────────────────────────────────────── */
.ex-checkpoint-header--v2 {
  display: flex;
  flex-direction: column;
  border: none;
}
.ex-checkpoint-v2-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 13px;
}
.ex-checkpoint-v2-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ex-checkpoint-v2-actions {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.ex-checkpoint-v2-actions ::v-deep .checkpoint-drop-down-menu {
  width: auto;
  flex-shrink: 0;
}
.ex-checkpoint-v2-actions ::v-deep .ex-menu-icon:hover,
.ex-checkpoint-v2-actions ::v-deep .ex-menu-icon {
  background: none !important;
  padding: 0;
  margin: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}
.ex-level-pill {
  position: relative;
}
.level-pill-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 36px;
  min-width: 118px;
  padding: 5px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  width: fit-content;
  outline: none;
  font: inherit;
}
.level-pill-trigger:disabled,
.level-pill-trigger.disabled {
  cursor: default;
  opacity: 1;
}
.level-pill-trigger:focus,
.level-pill-trigger:focus-visible {
  outline: none;
  box-shadow: none;
}
.level-pill-trigger.open {
  box-shadow: 0 0 0 3px #e9d7ff;
  border-color: #d6bbfb;
}
.level-pill-label {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
}
.level-pill-chevron {
  flex-shrink: 0;
  color: #667085;
  transition: transform 0.15s ease;
}
.level-pill-chevron.is-open {
  transform: rotate(180deg);
}
/* Apprentice — matches Figma exactly */
.level-pill-trigger.tone-apprentice {
  background: #fff5d3;
  border-color: #e58700;
  color: #e58700;
}
/* Other levels — color/hover/border from CheckpointLevelPicker */
.level-pill-trigger.tone-professional {
  background: #f3fff9;
  border-color: #00b813;
  color: #00b813;
}
.level-pill-trigger.tone-coach {
  background: #effeff;
  border-color: #00a3ff;
  color: #00a3ff;
}
.level-pill-trigger.tone-master {
  background: #f2f3ff;
  border-color: #2e1ec1;
  color: #2e1ec1;
}
.level-pill-trigger.tone-neutral {
  background: #ffffff;
  color: #101828;
}
.level-pill-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin: 4px 0 0;
  padding: 4px;
  width: 172px;
  list-style: none;
  z-index: 10;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  box-shadow:
    0px 4px 6px -2px #10182808,
    0px 12px 16px -4px #10182814;
}
.level-pill-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px;
  border-radius: 8px;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}
.level-pill-item.tone-apprentice {
  color: #e58700;
}
.level-pill-item.tone-apprentice:hover,
.level-pill-item.tone-apprentice.is-selected {
  background: #fff5d3;
}
.level-pill-item.tone-professional {
  color: #039855;
}
.level-pill-item.tone-professional:hover,
.level-pill-item.tone-professional.is-selected {
  background: #f3fff9;
}
.level-pill-item.tone-coach {
  color: #175cd3;
}
.level-pill-item.tone-coach:hover,
.level-pill-item.tone-coach.is-selected {
  background: #effeff;
}
.level-pill-item.tone-master {
  color: #7a5af8;
}
.level-pill-item.tone-master:hover,
.level-pill-item.tone-master.is-selected {
  background: #f2f3ff;
}
.level-pill-item.not-selectable {
  cursor: not-allowed;
  opacity: 0.8;
}
.level-pill-check {
  flex-shrink: 0;
  color: #7a5af8;
}

.ex-checkpoint-v2-status {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 2px 10px;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
}
.ex-checkpoint-v2-status--draft {
  background: #f9fafb;
  border: 1px solid #eaecf0;
  color: #344054;
}
.ex-checkpoint-v2-status--active {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  color: #067647;
}
.ex-checkpoint-v2-status--retired {
  border: 1.5px solid #475467;
  color: #344054;
}
.ex-checkpoint-v2-status--archived {
  background: #d92d20;
  border: 1.5px solid #475467;
  color: #ffffff;
}
.ex-checkpoint-v2-actions__chatter {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: url("../assets/cursorArrow.svg"), auto;
}
</style>

<style>
.ex-checkpoint-header {
  display: flex;
  flex-direction: column;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 10px 15px 10px 15px;
}
.ex-checkpoint-header-criteria {
  font-family: Inter;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  padding: 12px 10px 4px 1px;
}
.ex-checkpoint-header-criteria-editable {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}
.ex-checkpoint-header-criteria-editable-label {
  font-family: Inter;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
}
.ex-checkpoint-header-criteria-editable-criteria {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #666666;
}
.ex-checkpoint-header-criteria-editable-criteria .prefix {
  text-transform: capitalize;
}
.ex-checkpoint-crumb {
  padding: 6px 0px 12px 0px;
  display: flex;
  justify-content: space-between;
}
.ex-chatter-type-icon {
  display: grid;
  justify-content: end;
}
.ex-chatter-type-icon {
  padding: 6px;
  text-align: center;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
  position: relative;
  left: -10px;
}
.ex-chatter-type-icon:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}
.ex-right-side-checkpoint {
  display: flex;
  position: relative;
}
.ex-edit-checkpoint {
  width: 55px;
}
.ex-discard-checkpoint {
  width: 80px;
}
.ex-save-checkpoint {
  width: 61px;
}
.ex-checkpoint-header-instructions {
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;
  color: #475467;
  margin-left: 2px;
  margin-top: -5px;
}
.ex-checkpoint-picker {
  display: flex;
  position: relative;
  top: -10px;
}
.ex-checkpoint-picker .level-picker {
  margin-left: -5px;
}
.ex-checkpoint-criteria {
  margin-left: 8px;
  margin-top: -10px;
  margin-right: 8px;
}
.ex-checkpoint-criteria .check-criteria-input {
  margin-bottom: 0px !important;
}
.ex-right-buttons {
  display: flex;
  gap: 5px;
  position: relative;
  left: -12px;
  top: 4px;
}
</style>
